import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Constant } from "../../../helper/constant";
import { Chip } from "../../../components/ui";
import { formatDateTransaction, getInitialDate, toISODateInput } from "../../../helper/formatDate";
import PaginationFooter from "../../../components/footer/paginationFooter";
import SalesSummary from "../../sales/common_component/salesSummary";
import MedicalTreatmentRecordHeader from "../component/medicalTreatmentRecordHeader";
import MedicalTreatmentRecordTable from "../component/medicalTreatmentRecordTable";

const MedicalTreatmentRecord = () => {
  // =====================
  // Constant Definition
  // =====================

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [medicalTreatmentRecord, setMedicalTreatmentRecord] = useState([]);
  const [summaryTransaction, setSummaryTransaction] = useState(null);
  const [filterData, setFilterData] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [period, setPeriod] = useState("");

  const [formData, setFormData] = useState({
    start_date: toISODateInput(getInitialDate("daily")),
    end_date: toISODateInput(getInitialDate("daily")),
    doctor_treatment: {
      id: '',
      name: '',
    },
  })

  // =====================
  // Method
  // =====================

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  const onSearchMedicalRecordClicked = () => {
    setFilterData({
      start_date: formData.start_date,
      end_date: formData.end_date,
      medical_treatment_id: formData.doctor_treatment.id,
    })
  };

  const onDownloadMedicalRecordClicked = () => {
    if (filterData != null) {
      downloadMedicalRecord(filterData.start_date, filterData.end_date);
    }
  };

  // =====================
  // Network
  // =====================

  const getMedicalTreatmentRecordData = useCallback(async () => {
    if (!filterData) {
      return
    }

    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        start_date: formatDateTransaction(filterData.start_date, "daily"),
        end_date: formatDateTransaction(filterData.end_date, "daily"),
        page: page,
      });

      if (filterData.medical_treatment_id) {
        params.set('medical_treatment_id', filterData.medical_treatment_id);
      }

      const url = `${Constant.BASE_API_URL}/tindakan-medis-transaksi/data-tindakan-medis?${params.toString()}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });

      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }

      const json = await response.json();
      const data = json.data;

      if (!data.medical_transactions) {
        setMedicalTreatmentRecord([]);
      } else {
        setPeriod(data.period);
        setMedicalTreatmentRecord(data.medical_transactions);
        setSummaryTransaction(data.summary);
      }
      setTotalPage(data.total_pages);
    } catch (error) {
      setMedicalTreatmentRecord([]);
      setSummaryTransaction(null);
    } finally {
      setIsLoading(false);
    }
  }, [page, filterData, navigate]);

  const downloadMedicalRecord = async (startDate, endDate) => {
    try {
      const response = await fetch(
        Constant.BASE_API_URL + "/tindakan-medis-transaksi/download-record",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify({
            start_date: formatDateTransaction(filterData.start_date, "daily"),
            end_date: formatDateTransaction(filterData.end_date, "daily"),
            medical_treatment_id: filterData.medical_treatment_id,
          }),
        }
      );
      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const blob = await response.blob();

      // Create a link element to trigger the download
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const filename = `data-tindakan-medis-periode-${startDate}-${endDate}.xlsx`;
      link.download = filename;
      link.click();
    } catch (error) {
      alert("Terdapat Kesalahan Dalam Mendownload Data");
    }
  };

  useEffect(() => {
    getMedicalTreatmentRecordData();
  }, [getMedicalTreatmentRecordData])

  const renderFilterChip = () => {
    if (!filterData) return null
    return (
      <div className="mb-2 flex items-center space-x-3">
        {filterData.doctor_treatment_name && (
          <Chip>
            Tindakan medis: <strong className="ml-1">{filterData.doctor_treatment_name}</strong>
          </Chip>
        )}
        {period && (
          <Chip>
            Periode: <strong className="ml-1">{period}</strong>
          </Chip>
        )}
      </div>
    )
  }

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1 className="mb-4">Data Transaksi Tindakan Medis</h1>
      <MedicalTreatmentRecordHeader
        formData={formData}
        setFormData={setFormData}
        onSearchMedicalRecordClicked={onSearchMedicalRecordClicked}
        onDownloadMedicalRecordClicked={onDownloadMedicalRecordClicked}
      />
      {renderFilterChip()}
      <MedicalTreatmentRecordTable
        page={page}
        medicalTreatmentRecords={medicalTreatmentRecord}
      />
      <PaginationFooter
        isPreviousActive={page > 1}
        isNextActive={page < totalPage}
        onNextClick={onNextPageClicked}
        onPrevClick={onPreviousPageClicked}
      />
      {summaryTransaction != null && (
        <SalesSummary summaryData={summaryTransaction} />
      )}
    </div>
  );
};

export default MedicalTreatmentRecord;
